import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import {
  formatDateRu,
  formatTimeRu,
  toDateInputValue,
} from "../../../utils/appointmentLabels";
import ClubLifeHomeSection from "../common/ClubLifeHomeSection";
import {
  SummaryNearestSessions,
  SummaryNotifications,
} from "../AdminOverview/index";
import { staffJournalUrl, staffScheduleUrl } from "../../../utils/staffNav";
import { hubClientsPath } from "../../../utils/hubPaths";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import s from "../clinic.module.css";
import { SkeletonCard, useToast } from "../../ui";
import { IconCalendar, IconJournal, IconUsers } from "../../ui/icons";
import { resolveAttendanceForecastDisplay } from "../../../utils/sessionAttendanceInsight";
import { filterDashboardNotifications } from "../../../utils/notificationDisplay";
import { filterHomeSessions } from "../../../utils/homeNearestSessions";
import { usePlanAccess } from "../../../hooks/usePlanAccess";

function TrainerKpiCard({ label, value, hint, accent, icon: Icon, appMode }) {
  if (appMode) {
    return (
      <article
        className={
          accent
            ? `${aa.homeKpiCard} ${aa.homeKpiCardAccent}`
            : aa.homeKpiCard
        }
      >
        {Icon ? (
          <span className={aa.homeKpiIcon} aria-hidden>
            <Icon width={20} height={20} />
          </span>
        ) : null}
        <span className={aa.homeKpiValue}>{value}</span>
        <span className={aa.homeKpiLabel}>{label}</span>
        {hint ? <span className={aa.homeKpiHint}>{hint}</span> : null}
      </article>
    );
  }

  return (
    <article
      className={
        accent ? `${s.summaryKpi} ${s.summaryKpi_accent}` : s.summaryKpi
      }
    >
      {Icon ? (
        <span className={s.summaryKpiIcon} aria-hidden>
          <Icon width={20} height={20} />
        </span>
      ) : null}
      <span className={s.summaryKpiValue}>{value}</span>
      <span className={s.summaryKpiLabel}>{label}</span>
      {hint ? <span className={s.summaryKpiHint}>{hint}</span> : null}
    </article>
  );
}

export default function StaffOverview({ appMode = false }) {
  const navigate = useNavigate();
  const { profile, club } = useSelector((state) => state.auth);
  const plan = usePlanAccess();
  const toast = useToast();
  const today = toDateInputValue();
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clinicAPI.getSportToday();
      setDash(data);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  useAdminRefresh(load);

  const sessionsToday = dash?.sessions || [];
  const homeNearestSource =
    dash?.nearest_sessions?.length > 0
      ? dash.nearest_sessions
      : dash?.next_session
        ? [dash.next_session]
        : sessionsToday;
  const nearestList = filterHomeSessions(homeNearestSource);
  const expectedToday = dash?.expected_clients_today ?? 0;
  const attendanceForecast = resolveAttendanceForecastDisplay(
    dash?.attendance_forecast_today,
    expectedToday,
  );
  const markedToday = sessionsToday.reduce(
    (sum, sess) => sum + (sess.present_count ?? 0),
    0,
  );
  const notifications = filterDashboardNotifications(dash?.notifications || []);

  const openJournal = (sess) => {
    if (!sess) {
      navigate(staffJournalUrl({ date: today }));
      return;
    }
    navigate(
      staffJournalUrl({
        date: sess.session_date || today,
        sessionId: sess.id,
        groupId: sess.group?.id,
      }),
    );
  };

  const todayLabel = formatDateRu(`${today}T12:00:00`, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  if (loading) {
    return appMode ? (
      <div className={aa.homeDashboard}>
        <section className={aa.homeTodayZone}>
          <div className={aa.homeDayHead}>
            <h2>Сегодня</h2>
            <span>{todayLabel}</span>
          </div>
          <div className={aa.homeKpiStrip}>
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </section>
        <SkeletonCard />
      </div>
    ) : (
      <div className={s.summaryPage}>
        <div className={s.summaryKpiStrip}>
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <SkeletonCard />
      </div>
    );
  }

  const kpiStrip = (
    <section
      className={appMode ? aa.homeKpiStrip : s.summaryKpiStrip}
      aria-label="Показатели дня"
    >
      <TrainerKpiCard
        accent
        appMode={appMode}
        icon={IconUsers}
        label={attendanceForecast.label}
        value={attendanceForecast.value}
        hint={
          markedToday > 0
            ? `${markedToday} уже отмечено · ${attendanceForecast.hint}`
            : attendanceForecast.hint
        }
      />
      <TrainerKpiCard
        appMode={appMode}
        icon={IconCalendar}
        label="Занятий сегодня"
        value={sessionsToday.length}
        hint={
          nearestList[0]
            ? `ближайшее в ${formatTimeRu(nearestList[0].start_datetime)}`
            : "ваше расписание"
        }
      />
    </section>
  );

  const notificationsBlock = (
    <SummaryNotifications notifications={notifications} appMode={appMode} />
  );

  const nearestBlock = (
    <SummaryNearestSessions
      sessions={homeNearestSource}
      navigate={navigate}
      profile={profile}
      club={club}
      openJournal={openJournal}
      desktop={!appMode}
      calendarLink={staffScheduleUrl({ view: "day", date: today })}
      emptySchedulePath={staffScheduleUrl({ view: "month", date: today })}
      emptyDescription="Занятия появятся в расписании ваших групп"
    />
  );

  const quickActions = appMode ? (
    <section className={aa.homeQuickGrid} aria-label="Быстрые действия">
      <h2 className={aa.homeQuickGridTitle}>Быстрые действия</h2>
      <button
        type="button"
        className={aa.homeQuickTile}
        onClick={() => navigate(staffJournalUrl({ date: today }))}
      >
        <span className={aa.homeQuickTileIcon}>
          <IconJournal width={20} height={20} />
        </span>
        <span className={aa.homeQuickTileLabel}>Журнал</span>
      </button>
      <button
        type="button"
        className={aa.homeQuickTile}
        onClick={() => navigate(staffScheduleUrl({ view: "month", date: today }))}
      >
        <span className={aa.homeQuickTileIcon}>
          <IconCalendar width={20} height={20} />
        </span>
        <span className={aa.homeQuickTileLabel}>Расписание</span>
      </button>
      <button
        type="button"
        className={aa.homeQuickTile}
        onClick={() => navigate(hubClientsPath("/staff/app"))}
      >
        <span className={aa.homeQuickTileIcon}>
          <IconUsers width={20} height={20} />
        </span>
        <span className={aa.homeQuickTileLabel}>Клиенты</span>
      </button>
    </section>
  ) : (
    <section className={md.desktopQuickBlock} aria-label="Быстрые действия">
      <h2 className={md.desktopBlockTitle}>Быстрые действия</h2>
      <div className={md.desktopQuickGrid}>
        <button
          type="button"
          className={md.desktopQuickBtn}
          onClick={() => navigate(staffJournalUrl({ date: today }))}
        >
          <span className={md.desktopQuickIcon}>
            <IconJournal width={18} height={18} />
          </span>
          <span>Журнал</span>
        </button>
        <button
          type="button"
          className={md.desktopQuickBtn}
          onClick={() =>
            navigate(staffScheduleUrl({ view: "month", date: today }))
          }
        >
          <span className={md.desktopQuickIcon}>
            <IconCalendar width={18} height={18} />
          </span>
          <span>Расписание</span>
        </button>
        <button
          type="button"
          className={md.desktopQuickBtn}
          onClick={() => navigate(hubClientsPath("/staff/app"))}
        >
          <span className={md.desktopQuickIcon}>
            <IconUsers width={18} height={18} />
          </span>
          <span>Клиенты</span>
        </button>
      </div>
    </section>
  );

  if (appMode) {
    return (
      <div className={aa.homeDashboard}>
        {notificationsBlock}
        <section className={aa.homeTodayZone} aria-label="Сегодня">
          <div className={aa.homeDayHead}>
            <h2>Сегодня</h2>
            <span>{todayLabel}</span>
          </div>
          {kpiStrip}
        </section>
        {nearestBlock}
        {quickActions}
        {plan.clubLife ? (
        <ClubLifeHomeSection
          title="Клуб"
          limit={2}
          mode="admin"
          appMode
          feedLink="/staff/app/life"
        />
        ) : null}
      </div>
    );
  }

  return (
    <div className={`${s.summaryPage} ${md.desktopOverview}`}>
      {notificationsBlock}
      <section className={md.desktopToday} aria-label="Сегодня">
        <div className={md.desktopTodayHead}>
          <h2 className={md.desktopTodayTitle}>Сегодня</h2>
          <span className={md.desktopTodayDate}>{todayLabel}</span>
        </div>
        {kpiStrip}
      </section>
      {nearestBlock}
      {quickActions}
      {plan.clubLife ? (
      <ClubLifeHomeSection
        title="Клуб"
        limit={2}
        mode="admin"
        feedLink="/staff/app/life"
        desktop
      />
      ) : null}
    </div>
  );
}
